import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { ApiError } from '@app/api/ApiError';
import { API_BASE_URL } from '@app/api/client';
import { shellKeys, type Me } from '@app/api/hooks/shell';
import { get, post, type ApiBody } from '@app/api/typed';

export type LoginBody = ApiBody<'/api/v1/auth/login', 'post'>;

/**
 * Entrar com e-mail e senha (FR-001). O cookie de sessão vem no `Set-Cookie`;
 * o corpo é o mesmo `Me` de `getMe`, então já semeamos o cache — o console monta
 * autenticado sem uma segunda ida ao servidor.
 */
export function useLogin(): UseMutationResult<Me, ApiError, LoginBody> {
  const queryClient = useQueryClient();
  return useMutation<Me, ApiError, LoginBody>({
    mutationFn: (body) => post('/api/v1/auth/login', { body }),
    onSuccess: (me) => {
      queryClient.setQueryData(shellKeys.me, me);
    },
  });
}

/** URL de `googleStart`, com a rota de volta (o OIDC exige navegação de verdade). */
export function googleStartUrl(returnFor?: string | null): string {
  const base = `${API_BASE_URL}/api/v1/auth/google/start`;
  if (!returnFor) return base;
  return `${base}?return_to=${encodeURIComponent(returnFor)}`;
}

/**
 * **Entrar com Google** só aparece quando o provedor está configurado (FR-006).
 *
 * Não há flag pública no contrato: quem sabe é o próprio `googleStart`. A leitura
 * do desfecho da sondagem:
 *
 * - **resposta do servidor com erro** (409 `google_nao_configurado` sem client,
 *   404 se a rota não existir nesta versão) → esconder: não há para onde mandar
 *   o usuário;
 * - **falha de transporte** (o navegador seguiu o 302 para `accounts.google.com`
 *   e bateu no CORS) → mostrar: o redirect só existe porque há client;
 * - **sucesso** → mostrar.
 */
export function useAvailableGoogle(): UseQueryResult<boolean, never> {
  return useQuery<boolean, never>({
    queryKey: ['auth', 'google', 'disponivel'],
    queryFn: async () => {
      try {
        await get('/api/v1/auth/google/start');
        return true;
      } catch (erro) {
        return !(erro instanceof ApiError && erro.status >= 400);
      }
    },
    retry: false,
    // A configuração do provedor não muda durante a visita à tela de login.
    staleTime: Infinity,
  });
}
